import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import PwaInstallPrompt from "./components/PwaInstallPrompt";

// Public
import Home from './pages/home';
import LoginAuthentication from './pages/login-authentication';
import AuthCallback from './pages/auth-callback';
import TermsOfService from './pages/legal/TermsOfService';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import DeleteAccount from './pages/legal/DeleteAccount';
import TcnInstall from './pages/tcn-install';

// Creator portal (/my)
import CreatorLogin from './pages/my-login';
import CreatorDashboard from './pages/my-dashboard';
import CreatorProfile from './pages/my-profile';
import CreatorStats from './pages/my-stats';
import CreatorRules from './pages/my-rules';
import CreatorCashback from './pages/my-cashback';
import CreatorGuidelines from './pages/my-guidelines';
import CreatorAgencyRules from './pages/my-agency-rules';
import CreatorWelcome from './pages/my-welcome';
import CreatorPublicDashboard from './pages/my-public-dashboard';
import CreatorCalculator from './pages/my-calculator';
import CreatorAgencyStats from './pages/my-agency-stats';
import CreatorApply from './pages/my-apply';

// Admin
import AdminDashboard from './pages/admin-dashboard';
import AdminProfile from './pages/admin-profile';
import AdminFinance from './pages/admin-finance';
import AdminViolations from './pages/admin-violations';
import CreatorsManagement from './pages/creators-management';
import CreatorProfileDetail from './pages/creator-profile-detail';
import EnhancedManagersManagement from './pages/enhanced-managers-management';
import ManagerAssignmentGroups from './pages/manager-assignment-groups';
import ManagerProfileDetail from './pages/manager-profile-detail';
import ManagerList from './pages/manager-list';
import ManagerRates from './pages/manager-rates';
import ManagerObjectivesManagement from './pages/manager-objectives-management';
import ManagerPayments from './pages/manager-payments';
import ConfigurationManagement from './pages/configuration-management';
import PerformanceActuelle from './pages/performance-actuelle';
import PerformancesManagement from './pages/performances-management';
import NotificationsManagement from './pages/notifications-management';
import NotificationsRules from './pages/notifications-rules';
import CashbackManagement from './pages/cashback-management';
import CashbackPayments from './pages/cashback-payments';
import AdminObjectiveRules from './pages/admin/objective-rules';

// V2 (Sprint 2-3 — pilotage admin/today manager + creators/team)
import AdminV2Pilotage from './pages/admin-v2/pilotage';
import ManagerV2Today from './pages/manager-v2/today';
import AdminV2Creators from './pages/admin-v2/creators';
import AdminV2CreatorDetail from './pages/admin-v2/creators/Detail';
import ManagerV2Team from './pages/manager-v2/team';
import ManagerV2TeamDetail from './pages/manager-v2/team/Detail';
import AdminV2ManagerPayments from './pages/admin-v2/manager-payments';
import ManagerV2Payments from './pages/manager-v2/payments';
import AdminV2Finance from './pages/admin-v2/finance';
import AdminV2Cashback from './pages/admin-v2/cashback';
import AdminV2CashbackPayments from './pages/admin-v2/cashback-payments';
import AdminV2Performance from './pages/admin-v2/performance';
import ManagerV2Performance from './pages/manager-v2/performance';
import AdminV2DataInjections from './pages/admin-v2/data-injections';
import ManagerV2Actions from './pages/manager-v2/actions';
import ManagerV2Competition from './pages/manager-v2/competition';
import ManagerV2Live from './pages/manager-v2/live';
import ManagerV2Risk from './pages/manager-v2/risk';
import AdminV2Violations from './pages/admin-v2/violations';
import AdminV2Recruitment from './pages/admin-v2/recruitment';
import AdminV2QuitMonitor from './pages/admin-v2/quit-monitor';
import AdminV2Referrals from './pages/admin-v2/referrals';
import AdminV2Events from './pages/admin-v2/events';
import ManagerV2Recruit from './pages/manager-v2/recruit';
import AdminV2Tournaments from './pages/admin-v2/tournaments';
import AdminV2TournamentDetail from './pages/admin-v2/tournaments/Detail';
import ManagerV2Tournaments from './pages/manager-v2/tournaments';
import ManagerV2TournamentDetail from './pages/manager-v2/tournaments/Detail';
import AdminV2News from './pages/admin-v2/news';
import AdminV2Promo from './pages/admin-v2/promo';
import AdminV2AI from './pages/admin-v2/ai';
import ManagerV2AI from './pages/manager-v2/ai';
import V2Switcher from './components/V2Switcher';
import FeatureGuard from './components/FeatureGuard';

// Manager
import ManagerDashboard from './pages/manager-dashboard';
import ManagerCreatorsOverview from './pages/manager-creators-overview';
import ManagerCreatorProfileDetail from './pages/manager-creator-profile-detail';
import ManagerPerformance from './pages/manager-performance';
import ManagerPerformances from './pages/manager-performances';
import ManagerProfile from './pages/manager-profile';
import ManagerNotifications from './pages/manager-notifications';
import ManagerViolations from './pages/manager-violations';
import ManagerPaymentsView from './pages/manager-payments-view';
import ManagerRegionPerformance from './pages/manager-region-performance';

// Super Admin
import SuperAdminDashboard from './pages/superadmin';
import SuperAdminTenants from './pages/superadmin/tenants';
import SuperAdminTenantDetail from './pages/superadmin/tenants/Detail';
import SuperAdminBusinessRules from './pages/superadmin/business-rules';

// TCN tenant
import TcnLogin from './pages/tcn-login';
import TcnAdminUpload from './pages/tcn-admin-upload';
import TcnAdminImports from './pages/tcn-admin-imports';
import TcnAdminRecruitmentBonus from './pages/tcn-admin-recruitment-bonus';
import TcnAdminSettings from './pages/tcn-admin-settings';

// TCN Manager App (R1 — Modules 1, 2, 4 + Morning Briefing)
import TcnMeIndex from './pages/tcn-me';
import TcnBriefing from './pages/tcn-me/briefing';
import TcnCreatorsList from './pages/tcn-me/creators';
import TcnCreatorDetail from './pages/tcn-me/creators/Detail';
import TcnNewCreators from './pages/tcn-me/new-creators';
import TcnTiersPage from './pages/tcn-me/tiers';
import TcnPayoutPage from './pages/tcn-me/payout';
import TcnActionsPage from './pages/tcn-me/actions';
import TcnAlertsPage from './pages/tcn-me/alerts';

// === Phase 2 — kept on disk, routes disabled ===
// Tournament/poster modules will be re-enabled in Phase 2 once gated behind useFeature('tournament')
// import AdminTournaments from './pages/admin-tournaments';
// import TournamentDetail from './pages/admin-tournaments/TournamentDetail';
// import AdminTournamentV2 from './pages/admin-tournaments-v2';
// import ManagerTournaments from './pages/manager-tournaments';
// import TournamentPublic from './pages/tournament-public';
// import TournamentPublicV2 from './pages/tournament-public-v2';

const SUPER_ADMIN_ROLES = ['super_admin'];
const ADMIN_ROLES = ['admin', 'super_admin', 'tenant_admin', 'agency_owner'];
const MANAGER_ROLES = ['manager', 'region_manager', 'agent'];
// TCN admin pages = tenant + agency level only. super_admin doesn't manage tenant data
// directly (per separation of concerns — super_admin = platform, not data ops).
const TCN_ADMIN_ROLES = ['tenant_admin', 'agency_owner'];
const ALL_ROLES = [...ADMIN_ROLES, ...MANAGER_ROLES];

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/login-authentication" element={<LoginAuthentication />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/download-app" element={<TcnInstall />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Creator portal (/my) */}
          <Route path="/my/login" element={<CreatorLogin />} />
          <Route path="/my/welcome" element={<CreatorWelcome />} />
          <Route path="/my/public-dashboard" element={<CreatorPublicDashboard />} />
          <Route path="/my/calculator" element={<CreatorCalculator />} />
          <Route path="/my/agency-stats" element={<CreatorAgencyStats />} />
          <Route path="/my/apply" element={<CreatorApply />} />
          <Route path="/my/dashboard" element={<CreatorDashboard />} />
          <Route path="/my/profile" element={<CreatorProfile />} />
          <Route path="/my/stats" element={<CreatorStats />} />
          <Route path="/my/cashback" element={<CreatorCashback />} />
          <Route path="/my/rules" element={<CreatorRules />} />
          <Route path="/my/guidelines" element={<CreatorGuidelines />} />
          <Route path="/my/agency-rules" element={<CreatorAgencyRules />} />

          {/* Super Admin (system-level — separate from tenant admin pages) */}
          <Route path="/superadmin" element={<ProtectedRoute element={<SuperAdminDashboard />} allowedRoles={SUPER_ADMIN_ROLES} />} />
          <Route path="/superadmin/tenants" element={<ProtectedRoute element={<SuperAdminTenants />} allowedRoles={SUPER_ADMIN_ROLES} />} />
          <Route path="/superadmin/tenants/:id" element={<ProtectedRoute element={<SuperAdminTenantDetail />} allowedRoles={SUPER_ADMIN_ROLES} />} />
          <Route path="/superadmin/business-rules" element={<ProtectedRoute element={<SuperAdminBusinessRules />} allowedRoles={SUPER_ADMIN_ROLES} />} />

          {/* TCN tenant — alternate login + agency admin injection ops */}
          <Route path="/tcn/login" element={<TcnLogin />} />
          <Route path="/tcn/admin/upload" element={<ProtectedRoute element={<TcnAdminUpload />} allowedRoles={TCN_ADMIN_ROLES} />} />
          <Route path="/tcn/admin/imports" element={<ProtectedRoute element={<TcnAdminImports />} allowedRoles={TCN_ADMIN_ROLES} />} />
          <Route path="/tcn/admin/recruitment-bonus" element={<ProtectedRoute element={<TcnAdminRecruitmentBonus />} allowedRoles={TCN_ADMIN_ROLES} />} />
          <Route path="/tcn/admin/settings" element={<ProtectedRoute element={<TcnAdminSettings />} allowedRoles={TCN_ADMIN_ROLES} />} />

          {/* TCN Manager App R1 (Modules 1, 2, 4 + Morning Briefing) */}
          <Route path="/tcn/me" element={<ProtectedRoute element={<TcnMeIndex />} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/tcn/me/briefing" element={<ProtectedRoute element={<TcnBriefing />} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/tcn/me/creators" element={<ProtectedRoute element={<TcnCreatorsList />} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/tcn/me/creators/:id" element={<ProtectedRoute element={<TcnCreatorDetail />} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/tcn/me/new-creators" element={<ProtectedRoute element={<TcnNewCreators />} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/tcn/me/tiers" element={<ProtectedRoute element={<TcnTiersPage />} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/tcn/me/payout" element={<ProtectedRoute element={<TcnPayoutPage />} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/tcn/me/actions" element={<ProtectedRoute element={<TcnActionsPage />} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/tcn/me/alerts" element={<ProtectedRoute element={<TcnAlertsPage />} allowedRoles={MANAGER_ROLES} />} />

          {/* Admin */}
          {/* V1 dashboards stay on TCN; Generic + others bascule to V2 via V2Switcher (Sprint 2). */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute
                element={
                  <V2Switcher
                    V1={() => <AdminDashboard />}
                    V2={() => (
                      <FeatureGuard feature="pages.admin.pilotage">
                        <AdminV2Pilotage />
                      </FeatureGuard>
                    )}
                  />
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/v2/pilotage"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.admin.pilotage">
                    <AdminV2Pilotage />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          {/* /admin/creators : V2Switcher (TCN→V1, Generic→V2). V2 detail uses
              UUID at /admin/v2/creators/:id to avoid colliding with the V1
              :tiktok_id pattern. */}
          <Route
            path="/admin/creators"
            element={
              <ProtectedRoute
                element={
                  <V2Switcher
                    V1={() => <CreatorsManagement />}
                    V2={() => (
                      <FeatureGuard feature="pages.creators.list_admin">
                        <AdminV2Creators />
                      </FeatureGuard>
                    )}
                  />
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route path="/admin/creators/:tiktok_id" element={<ProtectedRoute element={<CreatorProfileDetail />} allowedRoles={ADMIN_ROLES} />} />
          <Route
            path="/admin/v2/creators"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.creators.list_admin">
                    <AdminV2Creators />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/v2/creators/:id"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.creators.detail_admin">
                    <AdminV2CreatorDetail />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          {/* Managers pages — wrapped behind FeatureGuard so super_admin can toggle them per tenant.
              TCN keeps these features OFF in the seed (Sprint 1), so TCN users get redirected by FeatureGuard.
              Generic keeps them ON by default. */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.managers.crud">
                    <EnhancedManagersManagement />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route path="/admin/managers" element={<Navigate to="/admin/users" replace />} />
          <Route
            path="/admin/managers/:id"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.managers.detail">
                    <ManagerProfileDetail />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/manager-list"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.managers.list_simple">
                    <ManagerList />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/groups"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.managers.assignments">
                    <ManagerAssignmentGroups />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route path="/admin/performance" element={<ProtectedRoute element={<PerformanceActuelle />} allowedRoles={ADMIN_ROLES} />} />
          <Route
            path="/admin/performances"
            element={
              <ProtectedRoute
                element={
                  <V2Switcher
                    V1={() => <PerformancesManagement />}
                    V2={() => (
                      <FeatureGuard feature="pages.performance.global">
                        <AdminV2Performance />
                      </FeatureGuard>
                    )}
                  />
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/v2/performance"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.performance.global">
                    <AdminV2Performance />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/data-injections"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.config.data_injections">
                    <AdminV2DataInjections />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/v2/data-injections"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.config.data_injections">
                    <AdminV2DataInjections />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/configuration"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.config.system">
                    <ConfigurationManagement />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.notifications.rules">
                    <NotificationsManagement />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/notifications/rules"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.notifications.rules">
                    <NotificationsRules />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/manager-rates"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.managers.rates">
                    <ManagerRates />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/finance"
            element={
              <ProtectedRoute
                element={
                  <V2Switcher
                    V1={() => <AdminFinance />}
                    V2={() => (
                      <FeatureGuard feature="pages.finance.dashboard">
                        <AdminV2Finance />
                      </FeatureGuard>
                    )}
                  />
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/v2/finance"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.finance.dashboard">
                    <AdminV2Finance />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/objectives"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.managers.objectives">
                    <ManagerObjectivesManagement />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route path="/admin/objective-rules" element={<ProtectedRoute element={<AdminObjectiveRules />} allowedRoles={['super_admin', 'tenant_admin', 'agency_owner', 'agency_admin']} />} />
          <Route
            path="/admin/cashback"
            element={
              <ProtectedRoute
                element={
                  <V2Switcher
                    V1={() => <CashbackManagement />}
                    V2={() => (
                      <FeatureGuard feature="pages.cashback.management">
                        <AdminV2Cashback />
                      </FeatureGuard>
                    )}
                  />
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/v2/cashback"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.cashback.management">
                    <AdminV2Cashback />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/profile"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.profile.admin">
                    <AdminProfile />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/violations"
            element={
              <ProtectedRoute
                element={
                  <V2Switcher
                    V1={() => <AdminViolations />}
                    V2={() => (
                      <FeatureGuard feature="pages.violations.admin">
                        <AdminV2Violations />
                      </FeatureGuard>
                    )}
                  />
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route path="/admin/v2/violations" element={<ProtectedRoute element={<FeatureGuard feature="pages.violations.admin"><AdminV2Violations /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/recruitment" element={<ProtectedRoute element={<FeatureGuard feature="pages.recruitment.admin"><AdminV2Recruitment /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/v2/recruitment" element={<ProtectedRoute element={<FeatureGuard feature="pages.recruitment.admin"><AdminV2Recruitment /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/quit-monitor" element={<ProtectedRoute element={<FeatureGuard feature="pages.quit_monitor.admin"><AdminV2QuitMonitor /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/referrals" element={<ProtectedRoute element={<FeatureGuard feature="pages.referrals.admin"><AdminV2Referrals /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/events" element={<ProtectedRoute element={<FeatureGuard feature="pages.events.admin"><AdminV2Events /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          {/* Tournaments (Sprint 9) — feature-gated OFF, manual data */}
          <Route path="/admin/tournaments" element={<ProtectedRoute element={<FeatureGuard feature="pages.tournaments.admin"><AdminV2Tournaments /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/tournaments/:id" element={<ProtectedRoute element={<FeatureGuard feature="pages.tournaments.admin"><AdminV2TournamentDetail /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/v2/tournaments" element={<ProtectedRoute element={<FeatureGuard feature="pages.tournaments.admin"><AdminV2Tournaments /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/v2/tournaments/:id" element={<ProtectedRoute element={<FeatureGuard feature="pages.tournaments.admin"><AdminV2TournamentDetail /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          {/* News / Promo / AI (Sprint 10) — feature-gated OFF */}
          <Route path="/admin/news" element={<ProtectedRoute element={<FeatureGuard feature="pages.news.admin"><AdminV2News /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/v2/news" element={<ProtectedRoute element={<FeatureGuard feature="pages.news.admin"><AdminV2News /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/promo" element={<ProtectedRoute element={<FeatureGuard feature="pages.promo.admin"><AdminV2Promo /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/v2/promo" element={<ProtectedRoute element={<FeatureGuard feature="pages.promo.admin"><AdminV2Promo /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/ai" element={<ProtectedRoute element={<FeatureGuard feature="pages.ai.admin"><AdminV2AI /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          <Route path="/admin/v2/ai" element={<ProtectedRoute element={<FeatureGuard feature="pages.ai.admin"><AdminV2AI /></FeatureGuard>} allowedRoles={ADMIN_ROLES} />} />
          {/* /admin/manager-payments : V2Switcher TCN→V1, Generic→V2 (cards + drill-down) */}
          <Route
            path="/admin/manager-payments"
            element={
              <ProtectedRoute
                element={
                  <V2Switcher
                    V1={() => <ManagerPayments />}
                    V2={() => (
                      <FeatureGuard feature="pages.managers.payments_admin">
                        <AdminV2ManagerPayments />
                      </FeatureGuard>
                    )}
                  />
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/v2/manager-payments"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.managers.payments_admin">
                    <AdminV2ManagerPayments />
                  </FeatureGuard>
                }
                allowedRoles={ADMIN_ROLES}
              />
            }
          />
          <Route
            path="/admin/cashback-payments"
            element={
              <ProtectedRoute
                element={
                  <V2Switcher
                    V1={() => <CashbackPayments />}
                    V2={() => (
                      <FeatureGuard feature="pages.cashback.payments">
                        <AdminV2CashbackPayments />
                      </FeatureGuard>
                    )}
                  />
                }
                allowedRoles={ALL_ROLES}
              />
            }
          />
          <Route
            path="/admin/v2/cashback-payments"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.cashback.payments">
                    <AdminV2CashbackPayments />
                  </FeatureGuard>
                }
                allowedRoles={ALL_ROLES}
              />
            }
          />

          {/* Manager */}
          <Route
            path="/manager/dashboard"
            element={
              <ProtectedRoute
                element={
                  <V2Switcher
                    V1={() => <ManagerDashboard />}
                    V2={() => (
                      <FeatureGuard feature="pages.manager.today">
                        <ManagerV2Today />
                      </FeatureGuard>
                    )}
                  />
                }
                allowedRoles={MANAGER_ROLES}
              />
            }
          />
          <Route
            path="/manager/v2/today"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.manager.today">
                    <ManagerV2Today />
                  </FeatureGuard>
                }
                allowedRoles={MANAGER_ROLES}
              />
            }
          />
          <Route path="/manager/creators" element={<ProtectedRoute element={<ManagerCreatorsOverview />} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/creators/:tiktok_id" element={<ProtectedRoute element={<ManagerCreatorProfileDetail />} allowedRoles={MANAGER_ROLES} />} />
          {/* V2 manager team (new in Sprint 3 — no V1 equivalent) */}
          <Route
            path="/manager/team"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.creators.list_manager">
                    <ManagerV2Team />
                  </FeatureGuard>
                }
                allowedRoles={MANAGER_ROLES}
              />
            }
          />
          <Route
            path="/manager/team/:id"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.creators.detail_manager">
                    <ManagerV2TeamDetail />
                  </FeatureGuard>
                }
                allowedRoles={MANAGER_ROLES}
              />
            }
          />
          <Route
            path="/manager/v2/team"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.creators.list_manager">
                    <ManagerV2Team />
                  </FeatureGuard>
                }
                allowedRoles={MANAGER_ROLES}
              />
            }
          />
          <Route
            path="/manager/v2/team/:id"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.creators.detail_manager">
                    <ManagerV2TeamDetail />
                  </FeatureGuard>
                }
                allowedRoles={MANAGER_ROLES}
              />
            }
          />
          <Route
            path="/manager/performance"
            element={
              <ProtectedRoute
                element={
                  <V2Switcher
                    V1={() => <ManagerPerformance />}
                    V2={() => (
                      <FeatureGuard feature="pages.performance.manager_drill">
                        <ManagerV2Performance />
                      </FeatureGuard>
                    )}
                  />
                }
                allowedRoles={MANAGER_ROLES}
              />
            }
          />
          <Route
            path="/manager/v2/performance"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.performance.manager_drill">
                    <ManagerV2Performance />
                  </FeatureGuard>
                }
                allowedRoles={MANAGER_ROLES}
              />
            }
          />
          <Route path="/manager/performances" element={<ProtectedRoute element={<ManagerPerformances />} allowedRoles={MANAGER_ROLES} />} />
          <Route
            path="/manager/notifications"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.notifications.manager_inbox">
                    <ManagerNotifications />
                  </FeatureGuard>
                }
                allowedRoles={MANAGER_ROLES}
              />
            }
          />
          <Route
            path="/manager/profile"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.profile.manager">
                    <ManagerProfile />
                  </FeatureGuard>
                }
                allowedRoles={MANAGER_ROLES}
              />
            }
          />
          {/* Manager V2 secondary pages (Sprint 7) */}
          <Route path="/manager/actions" element={<ProtectedRoute element={<FeatureGuard feature="pages.manager.actions"><ManagerV2Actions /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/v2/actions" element={<ProtectedRoute element={<FeatureGuard feature="pages.manager.actions"><ManagerV2Actions /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/competition" element={<ProtectedRoute element={<FeatureGuard feature="pages.manager.competition"><ManagerV2Competition /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/v2/competition" element={<ProtectedRoute element={<FeatureGuard feature="pages.manager.competition"><ManagerV2Competition /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/live" element={<ProtectedRoute element={<FeatureGuard feature="pages.manager.live"><ManagerV2Live /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/v2/live" element={<ProtectedRoute element={<FeatureGuard feature="pages.manager.live"><ManagerV2Live /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/risk" element={<ProtectedRoute element={<FeatureGuard feature="pages.manager.risk"><ManagerV2Risk /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/v2/risk" element={<ProtectedRoute element={<FeatureGuard feature="pages.manager.risk"><ManagerV2Risk /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/recruit" element={<ProtectedRoute element={<FeatureGuard feature="pages.recruitment.manager"><ManagerV2Recruit /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/v2/recruit" element={<ProtectedRoute element={<FeatureGuard feature="pages.recruitment.manager"><ManagerV2Recruit /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/tournaments" element={<ProtectedRoute element={<FeatureGuard feature="pages.tournaments.manager"><ManagerV2Tournaments /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/tournaments/:id" element={<ProtectedRoute element={<FeatureGuard feature="pages.tournaments.manager"><ManagerV2TournamentDetail /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/v2/tournaments" element={<ProtectedRoute element={<FeatureGuard feature="pages.tournaments.manager"><ManagerV2Tournaments /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/ai" element={<ProtectedRoute element={<FeatureGuard feature="pages.ai.manager"><ManagerV2AI /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/v2/ai" element={<ProtectedRoute element={<FeatureGuard feature="pages.ai.manager"><ManagerV2AI /></FeatureGuard>} allowedRoles={MANAGER_ROLES} />} />
          <Route path="/manager/violations" element={<ProtectedRoute element={<ManagerViolations />} allowedRoles={MANAGER_ROLES} />} />
          {/* /manager/payments : V2Switcher TCN→V1, Generic→V2 (cards + drill-down) */}
          <Route
            path="/manager/payments"
            element={
              <ProtectedRoute
                element={
                  <V2Switcher
                    V1={() => <ManagerPaymentsView />}
                    V2={() => (
                      <FeatureGuard feature="pages.managers.payments_view">
                        <ManagerV2Payments />
                      </FeatureGuard>
                    )}
                  />
                }
                allowedRoles={MANAGER_ROLES}
              />
            }
          />
          <Route
            path="/manager/v2/payments"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.managers.payments_view">
                    <ManagerV2Payments />
                  </FeatureGuard>
                }
                allowedRoles={MANAGER_ROLES}
              />
            }
          />
          <Route
            path="/manager/region-performance"
            element={
              <ProtectedRoute
                element={
                  <FeatureGuard feature="pages.performance.region">
                    <ManagerRegionPerformance />
                  </FeatureGuard>
                }
                allowedRoles={['region_manager']}
              />
            }
          />
          <Route path="/manager/cashback-payments" element={<ProtectedRoute element={<CashbackPayments />} allowedRoles={MANAGER_ROLES} />} />

          {/* Phase 2 — tournament/poster routes (kept commented for re-enable) */}
          {/* <Route path="/t/:slug" element={<TournamentPublicV2 />} /> */}
          {/* <Route path="/t-legacy/:slug" element={<TournamentPublic />} /> */}
          {/* <Route path="/admin/tournaments" element={<ProtectedRoute element={<AdminTournaments />} allowedRoles={ADMIN_ROLES} />} /> */}
          {/* <Route path="/admin/tournaments/:id" element={<ProtectedRoute element={<AdminTournamentV2 />} allowedRoles={ADMIN_ROLES} />} /> */}
          {/* <Route path="/admin/tournaments/legacy/:id" element={<ProtectedRoute element={<TournamentDetail />} allowedRoles={ADMIN_ROLES} />} /> */}
          {/* <Route path="/manager/tournaments" element={<ProtectedRoute element={<ManagerTournaments />} allowedRoles={MANAGER_ROLES} />} /> */}

          {/* Legacy redirects */}
          <Route path="/manager-dashboard" element={<Navigate to="/manager/dashboard" replace />} />
          <Route path="/enhanced-managers-management" element={<Navigate to="/admin/users" replace />} />
          <Route path="/manager-assignment-groups" element={<Navigate to="/admin/groups" replace />} />
          <Route path="/current-performance-analytics" element={<Navigate to="/admin/performance" replace />} />
          <Route path="/notifications-management" element={<Navigate to="/admin/notifications" replace />} />
          <Route path="/performances-management" element={<Navigate to="/admin/performances" replace />} />

          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
        <PwaInstallPrompt />
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
